import { CreateDatasetModal } from '@/components/DatasetModals';
import { ErrorPage } from '@/components/ErrorPage';
import { TablePageLayout } from '@/layouts/TablePageLayout';
import { useDatasetDataVersions } from '@/queries/dataset-data-versions';
import { useDatasetSchemaVersions } from '@/queries/dataset-schema-versions';
import { useDatasets, useDeleteDataset } from '@/queries/datasets';
import type { DatasetSummary } from '@/queries/datasets';
import { formatRelativeTime, getErrorMessage } from '@/utils';
import { formatSemanticVersion } from '@/utils/format';
import CopyOutlined from '@ant-design/icons/CopyOutlined';
import DatabaseOutlined from '@ant-design/icons/DatabaseOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import MoreOutlined from '@ant-design/icons/MoreOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Dropdown from 'antd/es/dropdown';
import type { MenuProps } from 'antd/es/menu';
import Typography from 'antd/es/typography';
import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { styled } from 'styled-components';
import { DataTable, useDataTableState } from 'ui-components';
import type { DataTableColumnType } from 'ui-components';

import type { DatasetDataVersion, DatasetSchemaVersion } from '@mai/types';

const { Text, Title } = Typography;

type DatasetRow = Pick<
  DatasetSummary,
  'datasetId' | 'name' | 'updatedAt'
> & {
  currentDataVersion: DatasetDataVersion['versionNo'] | null;
  currentSchemaVersion: DatasetSchemaVersion['versionNo'] | null;
};

const WarningText = styled.p`
  ${({ theme }) => `
  color: ${theme.colorError};
  font-size: ${theme.fontSize}px;
`}
`;

const DatasetNameLink = styled(Link)`
  ${({ theme }) => `
  font-weight: ${theme.fontWeightStrong};
  text-decoration: none;
  color: ${theme.colorLink};

  &:hover {
    color: ${theme.colorLinkHover};
  }
`}
`;

const ActionButton = styled(Button)`
  ${({ theme }) => `
  width: ${theme.controlHeight}px;
  height: ${theme.controlHeight}px;
`}
`;

const EmptyStateContainer = styled.div`
  ${({ theme }) => `
  text-align: center;
  padding: ${theme.paddingXL}px;
  background: ${theme.colorBgContainer};
  border-radius: ${theme.borderRadius}px;
  margin: ${theme.marginLG}px 0;
`}
`;



const GuidanceStep = styled.div`
  ${({ theme }) => `
  display: flex;
  align-items: flex-start;
  gap: ${theme.marginMD}px;
  margin-bottom: ${theme.marginLG}px;
  text-align: left;
  
  .step-number {
    background: ${theme.colorPrimary};
    color: white;
    width: ${theme.controlHeight}px;
    height: ${theme.controlHeight}px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: ${theme.fontWeightStrong};
    font-size: ${theme.fontSizeLG}px;
    flex-shrink: 0;
  }
  
  .step-content {
    flex: 1;
  }
`}
`;

export const DatasetsPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const { message, modal } = App.useApp();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { requestState } = useDataTableState({
    filterMap: {
      name: 'name',
    },
  });

  const {
    data: datasetsData,
    isLoading,
    error,
  } = useDatasets(
    {
      ...requestState,
      teamIds: teamId ? [teamId] : undefined,
    },
    {
      enabled: !!teamId,
    },
  );

  // Fetch the dataset data versions for the current ids
  const { data: datasetDataVersions } = useDatasetDataVersions(
    {
      datasetDataVersionIds:
        datasetsData?.items
          .map((dataset) => dataset.currentDataVersionId)
          .filter((id): id is string => id !== null) || [],
    },
    {
      enabled: !!datasetsData?.items,
    },
  );

  // Fetch the dataset schema versions for the current ids
  const { data: datasetSchemaVersions } = useDatasetSchemaVersions(
    {
      datasetSchemaVersionIds:
        datasetsData?.items
          .map((dataset) => dataset.currentSchemaVersionId)
          .filter((id): id is string => id !== null) || [],
    },
    {
      enabled: !!datasetsData?.items,
    },
  );

  const deleteDatasetMutation = useDeleteDataset();

  const tableData = useMemo(() => {
    const datasetDataVersionMap = new Map(
      datasetDataVersions?.items.map((dataVersion) => [
        dataVersion.datasetDataVersionId,
        dataVersion,
      ]) || [],
    );

    const datasetSchemaVersionMap = new Map(
      datasetSchemaVersions?.items.map((schemaVersion) => [
        schemaVersion.datasetSchemaVersionId,
        schemaVersion,
      ]) || [],
    );

    return datasetsData?.items.map((dataset) => ({
      ...dataset,
      currentDataVersion:
        (dataset.currentDataVersionId &&
          datasetDataVersionMap.get(dataset.currentDataVersionId)?.versionNo) ||
        null,
      currentSchemaVersion:
        (dataset.currentSchemaVersionId &&
          datasetSchemaVersionMap.get(dataset.currentSchemaVersionId)
            ?.versionNo) ||
        null,
    }));
  }, [datasetsData, datasetDataVersions, datasetSchemaVersions]);

  const handleCreateDataset = () => {
    setIsCreateModalOpen(true);
  };



  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleDatasetCreated = (_dataset: DatasetSummary) => {
    // Dataset created successfully - no redirect needed as we don't want to force user to view more details unless they want to
  };

  const handleCopyId = React.useCallback(
    async (id: string) => {
      try {
        await navigator.clipboard.writeText(id);
        void message.success('ID copied to clipboard');
      } catch (error) {
        void message.error('Failed to copy ID');
      }
    },
    [message],
  );

  const handleDeleteDataset = React.useCallback(
    (datasetId: string, datasetName: string) => {
      void modal.confirm({
        title: 'Delete Dataset',
        content: (
          <div>
            <p>
              Are you sure you want to delete the dataset{' '}
              <strong>"{datasetName}"</strong>?
            </p>
            <WarningText>
              <strong>Warning:</strong> This action cannot be undone. All data,
              schema versions, and data versions associated with this dataset
              will be permanently deleted.
            </WarningText>
          </div>
        ),
        okText: 'Delete Dataset',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            await deleteDatasetMutation.mutateAsync(datasetId);
            void message.success(
              `Dataset "${datasetName}" has been deleted successfully`,
            );
          } catch (error) {
            const errorMessage = getErrorMessage(
              error,
              'Failed to delete dataset',
            );
            void message.error(errorMessage);
          }
        },
      });
    },
    [message, modal, deleteDatasetMutation],
  );

  const columns: DataTableColumnType<DatasetRow>[] = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        render: (_, record) => (
          <DatasetNameLink to={`/team/${teamId}/datasets/${record.datasetId}`}>
            {record.name}
          </DatasetNameLink>
        ),
        sorter: true,
        filter: {
          type: 'search',
          placeholder: 'Search by name...',
        },
      },
      {
        title: 'Schema Version',
        dataIndex: 'currentSchemaVersion',
        key: 'currentSchemaVersion',
        width: 200,
        ellipsis: true,
        render: (version: number | string | null) =>
          version ? formatSemanticVersion(version) : '-',
      },
      {
        title: 'Data Version',
        dataIndex: 'currentDataVersion',
        key: 'currentDataVersion',
        width: 200,
        ellipsis: true,
        render: (version: number | string | null) =>
          version ? formatSemanticVersion(version) : '-',
      },
      {
        title: 'Last Updated',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 150,
        render: (date: Date) => formatRelativeTime(date),
        sorter: true,
      },
      {
        title: '',
        key: 'actions',
        align: 'center',
        width: 80,
        render: (_, record) => {
          const items: MenuProps['items'] = [
            {
              key: 'copy-id',
              icon: <CopyOutlined />,
              label: 'Copy ID',
              onClick: () => void handleCopyId(record.datasetId),
            },
            {
              type: 'divider',
            },
            {
              key: 'delete',
              icon: <DeleteOutlined />,
              label: 'Delete Dataset',
              danger: true,
              onClick: () => handleDeleteDataset(record.datasetId, record.name),
            },
          ];

          return (
            <Dropdown
              menu={{ items }}
              trigger={['click']}
              placement="bottomRight"
            >
              <ActionButton type="text" icon={<MoreOutlined />} size="small" />
            </Dropdown>
          );
        },
      },
    ],
    [teamId, handleCopyId, handleDeleteDataset],
  );

  // Check for missing teamId after all hooks
  if (!teamId) {
    return (
      <ErrorPage
        title="Team Not Found"
        message="Unable to load datasets. Please make sure you're accessing a valid team."
        backButtonText="Go to Teams"
        onBack={() => (window.location.href = '/')}
      />
    );
  }

  if (error) {
    return (
      <div>
        <h2>Error Loading Datasets</h2>
        <p>There was an error loading the datasets. Please try again.</p>
      </div>
    );
  }

  return (
    <>
      <TablePageLayout
        title="Datasets"
        description="Manage and explore your datasets."
        actions={[
          {
            label: 'Create Dataset',
            type: 'primary',
            icon: <PlusOutlined />,
            onClick: handleCreateDataset,
          },
        ]}
      >
        <DataTable<DatasetRow>
          columns={columns}
          data={tableData || []}
          loading={isLoading}
          rowKey="datasetId"
          maxVisibleFilters={3}
          pagination={{
            current: datasetsData?.pagination?.page || 1,
            pageSize: datasetsData?.pagination?.pageSize || 20,
            total: datasetsData?.pagination?.totalItems || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          emptyState={{
            icon: <DatabaseOutlined />,
            title: 'Welcome to Datasets!',
            description: (
              <EmptyStateContainer>
                <Text
                  type="secondary"
                  style={{
                    fontSize: '16px',
                    marginBottom: '24px',
                    display: 'block',
                  }}
                >
                  Datasets let you upload, analyze, and query your data with
                  AI-powered insights.
                </Text>

                <div style={{ marginBottom: '32px' }}>
                  <Title level={5} style={{ marginBottom: '16px' }}>
                    Get Started in 3 Steps
                  </Title>

                  <GuidanceStep>
                    <div className="step-number">1</div>
                    <div className="step-content">
                      <Text strong>Upload Your Data</Text>
                      <br />
                      <Text type="secondary">
                        Drag & drop CSV or Excel files. Our smart upload
                        automatically detects your data structure.
                      </Text>
                    </div>
                  </GuidanceStep>

                  <GuidanceStep>
                    <div className="step-number">2</div>
                    <div className="step-content">
                      <Text strong>Review & Process</Text>
                      <br />
                      <Text type="secondary">
                        Confirm your data looks correct, then process it for
                        fast queries and AI features.
                      </Text>
                    </div>
                  </GuidanceStep>

                  <GuidanceStep>
                    <div className="step-number">3</div>
                    <div className="step-content">
                      <Text strong>Analyze & Query</Text>
                      <br />
                      <Text type="secondary">
                        Query your data with SQL, get AI insights, and share
                        results with your team.
                      </Text>
                    </div>
                  </GuidanceStep>
                </div>
              </EmptyStateContainer>
            ),
            action: {
              label: 'Create Your First Dataset',
              onClick: handleCreateDataset,
              icon: <PlusOutlined />,
            },
            overrideOnFiltersPresent: {
              icon: <DatabaseOutlined />,
              title: 'No datasets match your filters',
              description: 'Try adjusting your filters to see more results.',
              action: undefined,
            },
          }}
        />
      </TablePageLayout>
      <CreateDatasetModal
        open={isCreateModalOpen}
        onCancel={handleCloseCreateModal}
        onComplete={handleDatasetCreated}
        teamId={teamId || ''}
      />
    </>
  );
};
